/**
 * ContentContext — kept as the abstraction layer for every page, but reverted
 * to serve the static bootstrap data from /data/mockData.js and
 * /data/calmFeaturesData.js instead of fetching from the backend.
 */
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import {
  categories as bootstrapCategories,
  posts as bootstrapPostsByCategory,
  breathingGroups as bootstrapBreathingGroups,
  siteInfo,
} from '../data/mockData';
import {
  programs as bootstrapPrograms,
  soundscapes as bootstrapSoundscapes,
} from '../data/calmFeaturesData';

const flattenBootstrapPosts = () => {
  const out = [];
  Object.values(bootstrapPostsByCategory || {}).forEach((arr) => {
    if (Array.isArray(arr)) out.push(...arr);
  });
  return out;
};

const ContentContext = createContext(null);

export const ContentProvider = ({ children }) => {
  const categories = bootstrapCategories;
  const posts = useMemo(() => flattenBootstrapPosts(), []);
  const breathingGroups = bootstrapBreathingGroups;
  const programs = bootstrapPrograms;
  const soundscapes = bootstrapSoundscapes;
  const reload = useCallback(async () => {}, []);

  const value = useMemo(() => {
    const getCategoryName = (id) => categories.find((c) => c.id === id)?.name || '';
    const getPostById = (id) => posts.find((p) => p.id === id);
    const getPostsByCategory = (catId) => posts.filter((p) => p.category === catId);
    const getAllPosts = () => posts;
    const getBreathingExerciseById = (id) => {
      for (const g of breathingGroups) {
        const ex = (g.exercises || []).find((e) => e.id === id);
        if (ex) return { ...ex, groupName: g.name, groupAccent: g.accent };
      }
      return null;
    };
    const getProgramById = (id) => programs.find((p) => p.id === id);
    const getSoundscapeById = (id) => soundscapes.find((s) => s.id === id);

    return {
      loaded: true, reload,
      categories, posts, breathingGroups, programs, soundscapes, siteInfo,
      getCategoryName, getPostById, getPostsByCategory, getAllPosts,
      getBreathingExerciseById, getProgramById, getSoundscapeById,
    };
  }, [categories, posts, breathingGroups, programs, soundscapes, reload]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within a ContentProvider');
  return ctx;
};
